// Port of care-system/local-ai/src/chat_clean.py — the CareSoft "dwh-raw" transcript arrives as
// "KH: ... | NV: ..." with attachment JSON, join/leave/transfer lines and bot canned replies.
// This turns it back into ordered customer/sale turns, dropping the noise. Pure and deterministic.

export interface CleanTurn {
  who: 'khach' | 'sale';
  text: string;
}

const ROLE_MAP: Record<string, 'khach' | 'sale'> = {
  kh: 'khach', khach: 'khach', 'khách': 'khach',
  nv: 'sale', sale: 'sale',
};
const ROLE_RE = /^\s*(KHÁCH|KHACH|KH|SALE|NV)\s*:\s*/i;
const SPLIT_RE = /\s*\|\s*(?=(?:KHÁCH|KHACH|KH|SALE|NV)\s*:)/i;

const SYSTEM_PREFIX_RE = /^(?:kích hoạt phân phối chat tự động|chuyển giao hội thoại|cuộc gọi (?:video|thoại) đã kết thúc|gặp tư vấn mua hàng)/i;
// The upstream transcript is cut at 1200 chars, so a trailing event can end mid-word ("đã thoát hội t").
const SYSTEM_EVENT_RE = /đã (?:tham gia|thoát|rời)(?: cuộc)?(?: h(?:ộ(?:i(?: t\S*)?)?)?)?\s*$/i;
// Click-to-message ads prefix the first customer turn: "messenger_ads ad_id.123 ad_name: <name|ids>".
const AD_REF_RE = /messenger_ads\s+ad_id\.\S+(?:\s+ad_name:\s*\S+(?:\s+\d{9,})*)?/i;
const BUTTON_LABEL_RE = /\b(?:gặp tư vấn mua hàng|gặp kỹ thuật\s*-\s*bảo hành)\b/i;
const CANNED_RE = /^(?:hiện tại nhân viên hỗ trợ không sẵn sàng|chào mừng (?:bạn|quý khách) đến với official account)/i;
// Livechat bot close-out appended to the last customer turn (see chat_clean.py).
const BOT_CLOSE_RE = /yêu cầu kết thúc hội thoại từ bot[\s\S]*$/i;
const HTML_BREAK_RE = /<br\s*\/?>/gi;
const GREETING_RE = /^(?:gearvn\s+mến\s+chào\s+quý\s+khách|mến\s+chào\s+quý\s+khách)\s*[!.,:;\-]*\s*/i;
const INTRO_RE = /^em\s+là\s+[^,.!?]{1,40}[,.]?\s*xin\s+phép\s+hỗ\s+trợ(?:\s+(?:mình|anh|chị|quý\s+khách))?\s*(?:ạ|nhé|nha)?\s*[!.,]*\s*/i;

const IMAGE_TYPES = new Set(['image', 'img', 'photo', 'picture', 'sticker']);
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'bmp']);
const TEMPLATE_KEYS = ['payload', 'buttons', 'template_type', 'elements'];

// Same rule as common.mask_pii: any phone-like substring becomes [SĐT]. The capture group keeps the
// leading non-digit so a single replace pass behaves like Python's negative lookbehind.
const PHONE_RE = /(^|[^\d])((?:\+?84[\s.\-]*|0)?[35789](?:[\s.\-]*\d){8})(?!\d)/g;

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function maskPii(text: string): string {
  return text.replace(PHONE_RE, (_match, lead: string) => `${lead}[SĐT]`);
}

type Chunk = { kind: 'text'; value: string } | { kind: 'json'; value: unknown };

function jsonChunks(text: string): Chunk[] {
  const parts: Chunk[] = [];
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf('{', i);
    if (start < 0) {
      parts.push({ kind: 'text', value: text.slice(i) });
      break;
    }
    if (start > i) parts.push({ kind: 'text', value: text.slice(i, start) });
    let depth = 0;
    let inStr = false;
    let escaped = false;
    let end = -1;
    for (let j = start; j < text.length; j++) {
      const ch = text[j];
      if (inStr) {
        if (escaped) escaped = false;
        else if (ch === '\\') escaped = true;
        else if (ch === '"') inStr = false;
        continue;
      }
      if (ch === '"') inStr = true;
      else if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) {
          end = j + 1;
          break;
        }
      }
    }
    if (end < 0) {
      parts.push({ kind: 'text', value: text.slice(start) });
      break;
    }
    const blob = text.slice(start, end);
    try {
      parts.push({ kind: 'json', value: JSON.parse(blob) });
    } catch {
      parts.push({ kind: 'text', value: blob });
    }
    i = end;
  }
  return parts;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasAny(obj: Record<string, unknown>, keys: readonly string[]): boolean {
  return keys.some(key => key in obj);
}

function attachmentMarker(value: unknown): string | null {
  if (!isRecord(value)) return null;
  let obj: Record<string, unknown> = value;
  let inner = obj.attachment;
  if (Array.isArray(inner)) inner = inner[0] ?? null;
  if (!isRecord(inner)) {
    inner = obj.attachments;
    if (Array.isArray(inner)) inner = inner[0] ?? null;
  }
  if (isRecord(inner)) {
    const fileKeys = ['fileName', 'file_name', 'fileType', 'file_type'];
    if (hasAny(inner, TEMPLATE_KEYS) && !hasAny(inner, fileKeys)) return null;
    obj = inner;
  } else {
    const identityKeys = ['fileName', 'file_name', 'fileType', 'file_type', 'type'];
    if (hasAny(obj, TEMPLATE_KEYS) && !hasAny(obj, identityKeys)) return null;
  }

  const name = String(obj.fileName ?? obj.file_name ?? obj.name ?? '').trim();
  const ftype = String(obj.fileType ?? obj.file_type ?? obj.type ?? '').trim().toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
  if (IMAGE_TYPES.has(ftype) || IMAGE_EXTS.has(ext)) return '[ảnh]';
  if (name || ftype) return '[file]';
  return null;
}

function cleanPlain(input: string): string {
  let text = collapse(input);
  if (!text) return '';
  text = text.replace(HTML_BREAK_RE, ' ').replace(BOT_CLOSE_RE, ' ');
  text = collapse(text.replace(AD_REF_RE, ' ').replace(BUTTON_LABEL_RE, ' '));
  if (!text || SYSTEM_PREFIX_RE.test(text) || SYSTEM_EVENT_RE.test(text) || CANNED_RE.test(text)) return '';
  text = text.replace(GREETING_RE, '');
  text = text.replace(INTRO_RE, '');
  text = collapse(text).replace(/^[\s\-|,;:]+|[\s\-|,;:]+$/g, '');
  return maskPii(text);
}

function cleanTurn(text: string): string | null {
  const texts: string[] = [];
  const markers: string[] = [];
  for (const chunk of jsonChunks(text)) {
    if (chunk.kind === 'text') {
      const cleaned = cleanPlain(chunk.value);
      if (cleaned) texts.push(cleaned);
    } else {
      const marker = attachmentMarker(chunk.value);
      if (marker) markers.push(marker);
    }
  }
  const body = collapse(texts.join(' '));
  if (body) return body;
  // An attachment only survives as a short marker when it is the whole turn.
  return markers[0] ?? null;
}

export function cleanMessage(text: unknown): string | null {
  return cleanTurn(String(text ?? ''));
}

export function cleanTranscript(text: unknown): CleanTurn[] {
  const raw = text === null || text === undefined ? '' : String(text);
  if (!raw.trim()) return [];
  const turns: CleanTurn[] = [];
  for (const segment of raw.split(SPLIT_RE)) {
    const match = segment.match(ROLE_RE);
    if (!match) continue;
    const who = ROLE_MAP[match[1].trim().toLowerCase()];
    if (!who) continue;
    const cleaned = cleanTurn(segment.slice(match[0].length));
    if (!cleaned) continue;
    const last = turns[turns.length - 1];
    if (last && last.who === who) last.text = collapse(`${last.text} ${cleaned}`);
    else turns.push({ who, text: cleaned });
  }
  return turns;
}
